import {useCallback, useEffect, useRef, useState} from 'react';

/** Calculate new pan to keep a focal point stationary after zoom change. */
function zoomToPoint(
    focalX: number,
    focalY: number,
    oldZoom: number,
    newZoom: number,
    oldPan: { x: number; y: number }
): { x: number; y: number } {
    const px = (focalX - oldPan.x) / oldZoom;
    const py = (focalY - oldPan.y) / oldZoom;
    return {x: focalX - px * newZoom, y: focalY - py * newZoom};
}

interface UseZoomPanOptions {
    maxZoom: number;
    minZoom?: number;
    wheelZoomFactor?: number;
    doubleClickZoomFactor?: number;
    /** Element to attach the non-passive wheel listener to. */
    wheelElementRef: React.RefObject<HTMLElement | null>;
    /** Element used for getBoundingClientRect to compute the zoom focal point. */
    focalPointRef: React.RefObject<HTMLElement | null>;
    /** Element whose transform is updated directly during drag (bypasses React for perf). */
    transformRef: React.RefObject<HTMLElement | null>;
    /** CSS selector — mousedown on matching elements will not start a drag. */
    ignoreSelector?: string;
    /** Called on click (not drag). Receives the mousedown event target. */
    onClick?: (target: HTMLElement) => void;
    /** Controls whether the wheel listener is attached. */
    enabled?: boolean;
}

export function useZoomPan({
    maxZoom,
    minZoom = 0.5,
    wheelZoomFactor = 1.1,
    doubleClickZoomFactor = 1.5,
    wheelElementRef,
    focalPointRef,
    transformRef,
    ignoreSelector,
    onClick,
    enabled = true,
}: UseZoomPanOptions) {
    // ── State (triggers renders for UI like cursor, zoom buttons, marker counter-scale) ──
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({x: 0, y: 0});
    const [isDragging, setIsDragging] = useState(false);

    // ── Refs for event handlers (no renders) ──
    const zoomRef = useRef(zoom);
    const panRef = useRef(pan);
    const maxZoomRef = useRef(maxZoom);
    const isDraggingRef = useRef(false);
    const dragStartRef = useRef({x: 0, y: 0});
    const dragStartPosRef = useRef({x: 0, y: 0});
    const hasMovedRef = useRef(false);
    const mouseDownTargetRef = useRef<HTMLElement | null>(null);
    const onClickRef = useRef(onClick);

    // Sync state → refs
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    useEffect(() => { panRef.current = pan; }, [pan]);
    useEffect(() => { maxZoomRef.current = maxZoom; }, [maxZoom]);
    useEffect(() => { onClickRef.current = onClick; }, [onClick]);

    // ── Mouse drag handlers ──

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (ignoreSelector && (e.target as HTMLElement).closest(ignoreSelector)) return;
        isDraggingRef.current = true;
        setIsDragging(true);
        hasMovedRef.current = false;
        mouseDownTargetRef.current = e.target as HTMLElement;
        dragStartRef.current = {x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y};
        dragStartPosRef.current = {x: e.clientX, y: e.clientY};
    }, [ignoreSelector]);

    // During drag: update transform directly on the DOM element (no React re-renders).
    // Pan state is synced to React on mouseUp.
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDraggingRef.current) return;
        const dx = Math.abs(e.clientX - dragStartPosRef.current.x);
        const dy = Math.abs(e.clientY - dragStartPosRef.current.y);
        if (dx > 3 || dy > 3) hasMovedRef.current = true;

        const newPan = {
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y,
        };
        panRef.current = newPan;

        if (transformRef.current) {
            transformRef.current.style.transform =
                `translate(${newPan.x}px, ${newPan.y}px) scale(${zoomRef.current})`;
            transformRef.current.style.transition = 'none';
        }
    }, [transformRef]);

    const handleMouseUp = useCallback(() => {
        if (!isDraggingRef.current) return;
        const wasClick = !hasMovedRef.current;
        isDraggingRef.current = false;
        setIsDragging(false);
        // Sync final pan position to React state (one render)
        setPan(panRef.current);
        // Restore CSS transition on the transform element
        if (transformRef.current) {
            transformRef.current.style.transition = 'transform 0.1s ease-out';
        }
        if (wasClick && mouseDownTargetRef.current) {
            onClickRef.current?.(mouseDownTargetRef.current);
        }
    }, [transformRef]);

    // ── Double-click zoom ──

    const handleDoubleClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        if (!focalPointRef.current) return;
        const rect = focalPointRef.current.getBoundingClientRect();
        const newZoom = Math.min(zoomRef.current * doubleClickZoomFactor, maxZoomRef.current);
        const newPan = zoomToPoint(
            e.clientX - rect.left, e.clientY - rect.top,
            zoomRef.current, newZoom, panRef.current
        );
        setZoom(newZoom);
        setPan(newPan);
    }, [focalPointRef, doubleClickZoomFactor]);

    // ── Button zoom (toward container center) ──

    const handleZoom = useCallback((direction: 'in' | 'out') => {
        if (!focalPointRef.current) return;
        const rect = focalPointRef.current.getBoundingClientRect();
        const factor = direction === 'in' ? 1.5 : 1 / 1.5;
        const newZoom = direction === 'in'
            ? Math.min(zoomRef.current * factor, maxZoomRef.current)
            : Math.max(zoomRef.current * factor, minZoom);
        const newPan = zoomToPoint(
            rect.width / 2, rect.height / 2,
            zoomRef.current, newZoom, panRef.current
        );
        setZoom(newZoom);
        setPan(newPan);
    }, [focalPointRef, minZoom]);

    const resetZoom = useCallback(() => {
        setZoom(1);
        setPan({x: 0, y: 0});
    }, []);

    // ── Wheel zoom (non-passive to preventDefault) ──

    useEffect(() => {
        const wheelEl = wheelElementRef.current;
        const focalEl = focalPointRef.current;
        if (!wheelEl || !focalEl || !enabled) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const factor = Math.pow(wheelZoomFactor, -e.deltaY / 100);
            const newZoom = Math.min(Math.max(minZoom, zoomRef.current * factor), maxZoomRef.current);
            const rect = focalEl.getBoundingClientRect();
            const newPan = zoomToPoint(
                e.clientX - rect.left, e.clientY - rect.top,
                zoomRef.current, newZoom, panRef.current
            );
            setZoom(newZoom);
            setPan(newPan);
        };

        wheelEl.addEventListener('wheel', handleWheel, {passive: false});
        return () => wheelEl.removeEventListener('wheel', handleWheel);
    }, [wheelElementRef, focalPointRef, wheelZoomFactor, minZoom, enabled]);

    return {
        zoom,
        pan,
        isDragging,
        isZoomed: zoom !== 1 || pan.x !== 0 || pan.y !== 0,
        handlers: {
            onMouseDown: handleMouseDown,
            onMouseMove: handleMouseMove,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseUp,
            onDoubleClick: handleDoubleClick,
        },
        handleZoom,
        resetZoom,
    };
}
